import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, RefreshCw, ChevronLeft, Edit, Plus, Target, Layers, Calculator, FileText, ListChecks, Search } from 'lucide-react';
import http from '../api/http';
import { useFiliale } from '../context/FilialeContext';
import BlueHeader from '../components/BlueHeader';

const TypesPersonnelConfig = () => {
  const { activiteId } = useParams();
  const navigate = useNavigate();
  const { filialeId, isConsolidated } = useFiliale();
  
  const [activite, setActivite] = useState(null);
  const [types, setTypes] = useState([]);
  const [filteredTypes, setFilteredTypes] = useState([]);
  const [editingType, setEditingType] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    libelle: '',
    mode_paiement: 0,
    heure_tps_plein: 0,
    remuneration_jr_ferie: 0,
    prime_anciennete: 0,
    variable_prorata: 0,
    sming: 0
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, [activiteId, filialeId, isConsolidated]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const [activitesRes, typesRes, personnelRes] = await Promise.all([
        http.get('/api/activites').catch(() => ({ data: [] })),
        http.get('/api/types-personnel').catch(() => ({ data: [] })),
        http.get('/api/personnel').catch(() => ({ data: [] }))
      ]);

      const allActivites = Array.isArray(activitesRes.data) ? activitesRes.data : [];
      const activite = allActivites.find(a => a.idactivites === parseInt(activiteId));
      setActivite(activite);

      const allTypes = Array.isArray(typesRes.data) ? typesRes.data : [];
      const allPersonnel = Array.isArray(personnelRes.data?.data) ? personnelRes.data.data : [];

      // Filtrer par activité
      let filteredTypes = allTypes.filter(t =>
        t.idactivite === parseInt(activiteId) ||
        t.idactivite === 0 ||
        t.idactivite === null
      );

      // Si contexte filiale spécifique, filtrer les types qui ont du personnel dans cette filiale
      // OU montrer tous les types si aucun personnel n'existe dans cette filiale
      if (!isConsolidated && filialeId && filialeId !== 'all') {
        const personnelInFiliale = allPersonnel.filter(p => p.filiale_id === parseInt(filialeId));
        const typeIdsInFiliale = [...new Set(personnelInFiliale.map(p => p.type_personnel_id))];

        // Si il y a du personnel dans la filiale, filtrer par ces types
        // Sinon, montrer tous les types de l'activité
        if (typeIdsInFiliale.length > 0) {
          filteredTypes = filteredTypes.filter(t => typeIdsInFiliale.includes(t.idtype_personnel));
        }
      }

      setTypes(filteredTypes);
      setFilteredTypes(filteredTypes);
    } catch (error) {
      console.error('Erreur chargement données:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (searchTerm) {
      const filtered = types.filter(type =>
        type.libelle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        type.idtype_personnel?.toString().includes(searchTerm)
      );
      setFilteredTypes(filtered);
    } else {
      setFilteredTypes(types);
    }
  }, [searchTerm, types]);

  const handleCreate = () => {
    setEditingType(null);
    setFormData({
      libelle: '',
      mode_paiement: 0,
      heure_tps_plein: 0,
      remuneration_jr_ferie: 0,
      prime_anciennete: 0,
      variable_prorata: 0,
      sming: 0,
      idactivite: parseInt(activiteId)
    });
    setShowModal(true);
  };

  const handleEdit = (type) => {
    setEditingType(type);
    setFormData({
      libelle: type.libelle,
      mode_paiement: type.mode_paiement || 0,
      heure_tps_plein: type.heure_tps_plein || 0,
      remuneration_jr_ferie: type.remuneration_jr_ferie || 0,
      prime_anciennete: type.prime_anciennete || 0,
      variable_prorata: type.variable_prorata || 0,
      sming: type.sming || 0,
      idactivite: type.idactivite
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      
      if (editingType) {
        await http.put(`/api/types-personnel/${editingType.idtype_personnel}`, formData);
        alert('Type de personnel modifié avec succès');
      } else {
        await http.post('/api/types-personnel', formData);
        alert('Type de personnel créé avec succès');
      }
      
      setShowModal(false);
      setEditingType(null);
      loadData();
    } catch (error) {
      console.error('Erreur soumission:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Erreur lors de la soumission';
      alert(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const getModeLabel = (mode) => {
    switch (mode) {
      case 0: return 'Fixe';
      case 1: return 'Fixe + variable pondérée';
      case 2: return 'Fixe + paiement à l\'acte';
      case 3: return 'Fixe + variable';
      default: return '';
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-lg text-gray-500">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <BlueHeader
        title={`Mode de Paiement : ${activite?.Libelle_activite || 'Activité'}`}
        subtitle="Configurer les modes de paiement par type de personnel"
        actions={[
          {
            label: 'Retour',
            icon: ChevronLeft,
            onClick: () => navigate('/activites'),
            primary: false
          },
          {
            label: 'Actualiser',
            icon: RefreshCw,
            onClick: loadData,
            primary: false
          }
        ]}
      />

      <div className="px-6 py-8">
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher un type de personnel..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
            />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800">Types de Personnel</h3>
            <p className="text-sm text-gray-500 mt-1">{filteredTypes.length} type(s) de personnel</p>
          </div>

          {filteredTypes.length === 0 ? (
            <div className="px-6 py-12 text-center text-gray-500">
              {searchTerm ? 'Aucun type de personnel trouvé' : 'Aucun type de personnel trouvé pour cette activité'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Libellé</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mode de paiement</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Heure présence</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Jours fériés</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions config.</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredTypes.map((type, index) => (
                    <tr key={type.idtype_personnel} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">{index + 1}</td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{type.libelle}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{getModeLabel(type.mode_paiement)}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{type.heure_tps_plein === 1 ? 'OUI' : 'NON'}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{type.remuneration_jr_ferie === 1 ? 'OUI' : 'NON'}</td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          {type.mode_paiement === 2 && (
                            <button
                              onClick={() => navigate(`/paiement-acte/${type.idtype_personnel}`)}
                              className="text-green-600 hover:text-green-800"
                              title="Formule du brut"
                            >
                              <Calculator className="h-4 w-4" />
                            </button>
                          )}
                          {type.mode_paiement === 1 && (
                            <button
                              onClick={() => navigate(`/note-ponderee/${type.idtype_personnel}`)}
                              className="text-purple-600 hover:text-purple-800"
                              title="Note pondérée"
                            >
                              <Calculator className="h-4 w-4" />
                            </button>
                          )}
                          {type.mode_paiement === 3 && (
                            <button
                              onClick={() => navigate(`/note-ponderee/${type.idtype_personnel}`)}
                              className="text-blue-600 hover:text-blue-800"
                              title="Formule de la variable"
                            >
                              <Calculator className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            onClick={() => navigate(`/indicateurs/type/${type.idtype_personnel}`)}
                            className="text-indigo-600 hover:text-indigo-800"
                            title="Indicateurs"
                          >
                            <Target className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => navigate(`/conditions-paie/${type.idtype_personnel}`)}
                            className="text-orange-600 hover:text-orange-800"
                            title="Définir les conditions"
                          >
                            <ListChecks className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleEdit(type)}
                          className="text-blue-600 hover:text-blue-800"
                          title="Modifier"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal création/modification */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-2xl rounded-xl bg-white p-6 shadow-2xl border border-purple-100">
            <div className="mb-4 flex items-center justify-between border-b border-purple-100 pb-4">
              <h3 className="text-lg font-bold text-purple-900">
                {editingType ? 'Modifier' : 'Créer'} un Type de Personnel
              </h3>
              <button type="button" onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-900">✕</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">Libellé *</label>
                  <input
                    type="text"
                    value={formData.libelle}
                    onChange={(e) => setFormData({ ...formData, libelle: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all"
                    required
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">Mode de paiement *</label>
                  <select
                    value={formData.mode_paiement}
                    onChange={(e) => setFormData({ ...formData, mode_paiement: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all"
                    required
                  >
                    <option value="0">Fixe</option>
                    <option value="1">Fixe + variable pondérée</option>
                    <option value="2">Fixe + paiement à l'acte</option>
                    <option value="3">Fixe + variable</option>
                  </select>
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">Heure de présence</label>
                  <select
                    value={formData.heure_tps_plein}
                    onChange={(e) => setFormData({ ...formData, heure_tps_plein: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all"
                  >
                    <option value="0">NON</option>
                    <option value="1">OUI</option>
                  </select>
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">Jours fériés</label>
                  <select
                    value={formData.remuneration_jr_ferie}
                    onChange={(e) => setFormData({ ...formData, remuneration_jr_ferie: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all"
                  >
                    <option value="0">NON</option>
                    <option value="1">OUI</option>
                  </select>
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">Prime d'ancienneté</label>
                  <select
                    value={formData.prime_anciennete}
                    onChange={(e) => setFormData({ ...formData, prime_anciennete: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all"
                  >
                    <option value="0">OUI</option>
                    <option value="1">NON</option>
                  </select>
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">Variable au prorata</label>
                  <select
                    value={formData.variable_prorata}
                    onChange={(e) => setFormData({ ...formData, variable_prorata: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all"
                  >
                    <option value="0">NON</option>
                    <option value="1">OUI</option>
                  </select>
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">Calcul du SMIC</label>
                  <select
                    value={formData.sming}
                    onChange={(e) => setFormData({ ...formData, sming: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all"
                  >
                    <option value="0">OUI</option>
                    <option value="1">NON</option>
                  </select>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                >
                  {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TypesPersonnelConfig;
